import * as Sentry from '@sentry/react'
import { QueryClient } from '@tanstack/react-query'

type QueryStateLike = {
    status?: string
    error?: unknown
}

type QueryCacheEventLike = {
    type?: unknown
    query?: {
        state?: QueryStateLike
        queryHash?: string
    }
}

function isQueryErrorEvent(event: unknown): event is Required<QueryCacheEventLike> & {
    type: 'updated' | 'observerResultsUpdated'
    query: { state: QueryStateLike & { status: string } }
} {
    if (!event || typeof event !== 'object') {
        return false
    }

    const { type, query } = event as QueryCacheEventLike

    if (type !== 'updated' && type !== 'observerResultsUpdated') {
        return false
    }

    if (!query || typeof query !== 'object' || !query.state || typeof query.state !== 'object') {
        return false
    }

    return true
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 2,
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
        },
    },
})

queryClient.getQueryCache().subscribe((event) => {
    if (!isQueryErrorEvent(event)) {
        return
    }

    const { query } = event
    const { state } = query

    if (state.status !== 'error' || !state.error) {
        return
    }

    const error = state.error instanceof Error ? state.error : new Error(String(state.error))

    Sentry.withScope((scope) => {
        scope.setTag('source', 'react-query')
        scope.setContext('react_query', {
            queryHash: query.queryHash,
            status: state.status,
        })
        Sentry.captureException(error)
    })
})
